/**
 * フォーム編集の共通ステート (2026-07-28)。
 *
 * ビルダー (FormBuilderPage) と 設定 (FormSettingsPage) の 2 画面が同じ forms/{id} を
 * 編集するため、読み込み・dirty 管理・保存ペイロードをここに一本化する。
 *
 * ★重要: 保存は「全項目を明示列挙して update」する方式なので、片方の画面のペイロードに
 * 項目が欠けていると、その画面で保存した瞬間にもう片方の設定が消える。
 * 保存対象の追加は必ず buildFormUpdatePayload() だけに書くこと。
 */
import { useEffect, useState } from 'react';

import { toast } from '@/components/ui/toast';
import { useForm, useUpdateForm, type FormRecord } from '@/hooks/useForms';

/**
 * forms/{id} に書き戻すフィールド一覧（単一情報源）。
 * ここに無いフィールドは画面で編集しても保存されない。
 */
export function buildFormUpdatePayload(form: FormRecord) {
  return {
    name: form.name,
    title: form.title,
    description: form.description,
    elements: Array.isArray(form.elements) ? form.elements : [],
    thanksMessage: form.thanksMessage,
    redirectUrl: form.redirectUrl,
    theme: form.theme,
    status: form.status,
    publicStartAt: form.publicStartAt ?? null,
    publicEndAt: form.publicEndAt ?? null,
    contactTags: form.contactTags ?? [],
    // 顧客情報の更新ポリシー (2026-07-28)
    contactOverwritePolicy: form.contactOverwritePolicy ?? 'fill',
    // 定員設定 (2026-05)
    capacity: form.capacity ?? null,
    capacityFullMessage: form.capacityFullMessage ?? '',
    showRemainingCount: !!form.showRemainingCount,
    // 営業ツール (Phase 1+)
    ...(form.tag ? { tag: form.tag } : {}),
    dealStageHint: form.dealStageHint ?? null,
    primaryCategoryId: form.primaryCategoryId ?? null,
    primaryCategory: form.primaryCategory ?? null,
    category: form.category ?? null,
    // 弊社紹介フラグ (v3.2.29+): sales フォームのみ意味あり
    isCompanyReferral: !!form.isCompanyReferral,
    // v3.2.28+: SA ポータル表示可否 (sales フォームのみ意味あり、既定: 表示)
    visibleToSa: form.visibleToSa !== false,
    // 自動返信で送付する資料 (Phase 8): document_requested フォームのみ意味あり
    documentId: form.documentId ?? null,
    // 個別説明枠の表示対象 (?sa= 無しのときの部署フィルタ)
    briefingDepartmentFilter: form.briefingDepartmentFilter ?? null,
  };
}

export interface FormEditor {
  form: FormRecord | null;
  isLoading: boolean;
  dirty: boolean;
  saving: boolean;
  update: (patch: Partial<FormRecord>) => void;
  save: () => Promise<void>;
}

export function useFormEditor(id: string | undefined): FormEditor {
  const { data: original, isLoading } = useForm(id);
  const updateMutation = useUpdateForm();
  const [form, setForm] = useState<FormRecord | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (original && !form) setForm(original);
  }, [original, form]);

  const update = (patch: Partial<FormRecord>) => {
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));
    setDirty(true);
  };

  const save = async () => {
    if (!id || !form) return;
    // 質問フィールドのラベルは必須（ビルダー側で追加した空ラベルを設定画面から保存させない）
    const elements = Array.isArray(form.elements) ? form.elements : [];
    if (
      elements.some((e) => e.kind === 'field' && !(e.label ?? '').trim())
    ) {
      toast.error('すべての質問フィールドにラベルを入力してください');
      return;
    }
    setSaving(true);
    try {
      await updateMutation.mutateAsync({
        id,
        data: buildFormUpdatePayload(form),
      });
      toast.success('保存しました');
      setDirty(false);
    } catch (err) {
      toast.error(
        `保存失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setSaving(false);
    }
  };

  return { form, isLoading, dirty, saving, update, save };
}
